import React, { useState, useEffect } from 'react'
import { Box, Flex, Group, Button, Radio, Paper, Text, CheckIcon, SimpleGrid, Textarea, Switch } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { getOrderDetail } from '../../../../../store/orderDetail'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'
import { getAllStatusesList, storeStatuses } from '../../../../../services/status'
import LoadingData from '../../../../ui/LoadingData'
import dayjs from 'dayjs'

const defaultCondition = {
  salesDivision: [{ id: 9 }, { id: 37 }, { id: 39 }, { id: 44 }, { id: 45 }, { id: 54 }, { id: 55 }, { id: 68 }, { id: 78 }],
  purchaseDivision: [{ id: 10 }, { id: 46 }, { id: 81 }],
  logisticDivision: [{ id: 11 }, { id: 12 }, { id: 47 }],
  aftersalesDivision: [{ id: 13 }, { id: 40 }, { id: 79 }, { id: 80 }],
}

const defaultVal = {
  order_id: null,
  status_id: null,
  status_type: 2,
  date: null,
  notify: false,
  notes: null,
}

const FormAdjustOrderStatus = ({ onCloseForm }) => {
  const { summary } = useSelector(state => state.order_detail)
  const dispatch = useDispatch()
  const params = useParams()
  const orderId = params.order_id
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [orderStatusList, setOrderStatusList] = useState([])
  const [formData, setFormData] = useState(defaultVal)
  const [loadingForm, setLoadingForm] = useState(false)

  const submitUpdateOrderStatus = async (form) => {
    setLoadingForm(true)
    const payload = {
      order_id: form.order_id,
      status_id: Number(form.status_id),
      status_type: 2,
      date: form.date !== null ? dayjs(form.date).format('YYYY-MM-DD hh:mm:ss') : null,
      notify: form.notify,
      notes: form.notes,
    }
    try {
      const response = await storeStatuses(payload)
      if (response.success) {
        onCloseForm()
        dispatch(getOrderDetail(orderId))
        notificationSuccess('Update Order Successfully', 'This data has been updated')
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      notificationError('Update Order Error', `${errorMessage ? errorMessage : 'Please recheck your form'}`)
    } finally {
      setLoadingForm(false)
    }
  }

  const handleGetOrderStatusList = async () => {
    setLoadingStatus(true)
    const params = {
      'filter[status_type][is]': 2
    }
    try {
      const response = await getAllStatusesList(params)
      const dataStatus = response.data

      const handleFilterStatus = (data, division) => {
        // eslint-disable-next-line
        const filterData = data.filter((val) => {
          let data = division
          if (data.length > 0) {
            return data.find(value => value.id === val.attributes.id)
          }
        })
        return filterData
      }

      const divisionSales = handleFilterStatus(dataStatus, defaultCondition.salesDivision)
      const divisionPurchase = handleFilterStatus(dataStatus, defaultCondition.purchaseDivision)
      const divisionLogistic = handleFilterStatus(dataStatus, defaultCondition.logisticDivision)
      const divisionAftersales = handleFilterStatus(dataStatus, defaultCondition.aftersalesDivision)
      
      const orderStatusByDivision = [
        {
          name: 'Sales Division',
          data: divisionSales,
        },
        {
          name: 'Purchase & Warehouse Division',
          data: divisionPurchase,
        },
        {
          name: 'Logistic Division',
          data: divisionLogistic,
        },
        {
          name: 'Aftersales Division',
          data: divisionAftersales,
        },
      ]

      setOrderStatusList(orderStatusByDivision)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingStatus(false)
    }
  }

  useEffect(() => {
    handleGetOrderStatusList()
  }, [])

  useEffect(() => {
    if (summary) {
      handleSetForm(summary)
    }
    // eslint-disable-next-line
  }, [summary])

  const handleSetForm = (value) => {
    const dataDetail = {
      order_id: orderId,
      status_id: value.orderStatusId.toString(),
      status_type: 2,
      date: value.additionalOrderStatus ? new Date(value.additionalOrderStatus.date) : null,
      notify: true,
      notes: value.additionalOrderStatus ? value.additionalOrderStatus.notes : null,
    }
    setFormData(dataDetail)
  }

  const handleChangeForm = (name, val) => {
    console.log(val)
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const mappingFormStatusOrder = (orderStatus) => {
    if (orderStatus.length > 0) {

      const mappingDivisionStatusOrder = (dataStatus) => {

        const mappingDataStatusOrder = (data) => {
          // eslint-disable-next-line
          const remapValue = data.map((val, index) => {
            return (
              <Paper shadow="xs" radius="md" withBorder p="sm" key={index}>
                <Radio size='sm' icon={CheckIcon} value={val.attributes.id.toString()} label={<Text fz={12} fw='500' tt='capitalize' c='gray.7'>{val.attributes.name}</Text>} />
              </Paper> 
            )
          })
          return remapValue
        }

        const remapForm = dataStatus.map((val, index) => {
          return (
            <Paper shadow="xs" withBorder p="sm" mb='md' key={index}>
              <Radio.Group
                name="status_id"
                value={formData.status_id}
                onChange={(val) => handleChangeForm('status_id', val)}
                label={<Text fz={12} c='gray.6' fw={500} mb={4}>{val.name}</Text>}
                icon={CheckIcon}
                size='xs'
              >
                <SimpleGrid cols={3} spacing="xs" verticalSpacing="xs">
                  {mappingDataStatusOrder(val.data)}
                </SimpleGrid>
            </Radio.Group>
            </Paper>
          )
        })
        return remapForm
      }

      return (
        <Box>
          <Box mb='md'>
            {mappingDivisionStatusOrder(orderStatus)}
          </Box>
          <Box mb='md'>
            <Textarea
              name='notes'
              label="Additional Notes"
              placeholder="Input additional notes"
              value={formData.notes}
              onChange={(val) => handleChangeForm('notes', val.target.value)}
              size='xs'
              autosize
              minRows={4}
              maxRows={4}
            />
          </Box>
          <Box mb='md'>
            <DateTimePicker
              value={formData.date}
              onChange={(val) => handleChangeForm('date', val)}
              withSeconds
              valueFormat="DD/MM/YYYY hh:mm:ss A"
              label="Pick date and time"
              placeholder="Pick date and time"
              size='xs'
            />
          </Box>
          <Box mb='md'>
            <Paper shadow="xs" withBorder p="md">
              <Switch
                checked={formData.notify}
                onChange={(val) => handleChangeForm('notify', val.currentTarget.checked)}
                label="Notify Customer"
                description="If you enabled this, Customer will be receive Notification Message."
                size="sm"
              />
            </Paper>
          </Box>
          <Box mt={20}>
            <Flex justify='flex-end'>
              <Group>
                <Button size='xs' variant="outline" onClick={onCloseForm}>Close</Button>
                <Button size='xs' loading={loadingForm} variant="filled" onClick={() => submitUpdateOrderStatus(formData)}>{summary ? 'Update' : 'Create'}</Button>
              </Group>
            </Flex>
          </Box>
        </Box>
      )
    }
  }

  return (
    <Box>
      {loadingStatus ? <LoadingData /> : mappingFormStatusOrder(orderStatusList)}
    </Box>
  )
}

export default FormAdjustOrderStatus